import { useCallback } from "react";

import type { CustomerDetailsPayload } from "../../../../../types";

import { useSalesContext } from "../../../../../sales-context/SalesContext";
import { getHashedValue, removeCountryCode, validatePhoneNumber } from "../../../../../shared-utils";
import { useAnalytics } from "../../../../../shared-utils/analytics/withPolymorphicClickAnalytics";
import {
    useCallDetailsStore,
    useVerifiedCustomerStore,
    useVerificationFlowStore,
    useDetectedCuesStore,
} from "../../../../../stores";
import { useRapportBuildingStore } from "../../../../../stores/useRapportBuildingStore";
import { buildConfirmedCustomerData } from "../utils/buildConfirmedCustomerData";
import { buildGaiaSessionProperties, sanitizeGaiaSessionContextProperties } from "../utils/buildGaiaSessionProperties";
import { buildPartnerToolUrl } from "../utils/buildPartnerToolUrl";
import { buildSupportedProfileAnalyticsPayload } from "../utils/buildSupportedProfileAnalyticsPayload";
import { determineCustomerVerificationStatus } from "../utils/determineCustomerVerificationStatus";
import { env } from "../utils/environment";

export interface ConfirmCustomerVerificationParams {
    sessionId: string;
    customerName: string;
    mdn: string;
    mdnFromTask: string;
    verificationType: "phoneNumber" | "billingAccountNumberAndPin";
    customerData: CustomerDetailsPayload | undefined;
    showAdditionalDetailsEmailInput: boolean;
    showAdditionalDetailsOrderIdInput: boolean;
    showAdditionalDetailsAccountNumberInput: boolean;
    additionalDetailsEmail: string;
    additionalDetailsOrderId: string;
    additionalDetailsAccountNumber: string;
    additionalDetailsMdn: string;
    billingAccountNumber: string;
    currentSubscriptions: string[] | undefined;
    flushDebouncedUpdates: () => void;
}

export interface UseConfirmCustomerVerificationReturn {
    /**
     * Handles the confirm button click. Updates stores, sends analytics,
     * updates Gaia session context, and opens partner tool if applicable.
     */
    confirmCustomerVerification: (params: ConfirmCustomerVerificationParams) => Promise<void>;
}

export function useConfirmCustomerVerification(): UseConfirmCustomerVerificationReturn {
    const {
        partner,
        partnerConfig,
        taskType,
        taskData,
        sessionId: contextSessionId,
        gaiaWebsocket,
        logger,
    } = useSalesContext();
    const { dispatcher } = useAnalytics();
    const { partnerToolUrl } = partnerConfig;
    const { isVoice } = taskType;

    const confirmCustomerVerification = useCallback(
        async (params: ConfirmCustomerVerificationParams) => {
            const {
                sessionId,
                customerName,
                mdn,
                mdnFromTask,
                verificationType,
                customerData,
                showAdditionalDetailsEmailInput,
                showAdditionalDetailsOrderIdInput,
                showAdditionalDetailsAccountNumberInput,
                additionalDetailsEmail,
                additionalDetailsOrderId,
                additionalDetailsAccountNumber,
                additionalDetailsMdn,
                billingAccountNumber,
                currentSubscriptions,
                flushDebouncedUpdates,
            } = params;

            if (!customerName || (verificationType === "phoneNumber" && !mdn) || !gaiaWebsocket) {
                return;
            }

            flushDebouncedUpdates();

            const confirmedData = buildConfirmedCustomerData({
                verificationType,
                mdn,
                additionalDetailsMdn,
                customerName,
                customerData,
                showAdditionalDetailsEmailInput,
                additionalDetailsEmail,
                showAdditionalDetailsOrderIdInput,
                additionalDetailsOrderId,
                showAdditionalDetailsAccountNumberInput,
                additionalDetailsAccountNumber,
                billingAccountNumber,
                currentSubscriptions,
            });

            const {
                confirmedMdn,
                confirmedEmail,
                confirmedAccountNumber,
                confirmedAdditionalDetailsAccountNumber,
                customerToSave,
            } = confirmedData;

            const lookupMdnPresent = !!customerData?.primaryAccountHolder?.mdn;
            const mdnSource: "form" | "lookup" | "none" = confirmedMdn ? "form" : lookupMdnPresent ? "lookup" : "none";
            logger.info(
                {
                    partner,
                    sessionId,
                    verificationType,
                    mdnSource,
                    primaryAccountHolderMdnPresent: lookupMdnPresent,
                    confirmedMdnPresent: !!confirmedMdn,
                },
                "Customer verification confirmed — mdn resolution",
            );

            const formattedMdn = removeCountryCode(confirmedMdn);

            if (customerData?.previousDevices) {
                useDetectedCuesStore.getState().processPreviouslyDetectedCues(sessionId, customerData.previousDevices);
                useRapportBuildingStore.getState().updateLocation({
                    city: customerData.primaryAccountHolder?.address?.City,
                    state: customerData.primaryAccountHolder?.address?.StateCode,
                    timezone: customerData.primaryAccountHolder?.address?.TimeZone?.Name,
                });
            }

            const { setCustomer, setAsurionAgreements, setPartnerAgreements } = useVerifiedCustomerStore.getState();
            setCustomer(sessionId, customerToSave, customerData?.deviceActivation);
            setAsurionAgreements(sessionId, customerData?.asurionAgreements);
            setPartnerAgreements(sessionId, customerData?.partnerAgreements);

            if (showAdditionalDetailsAccountNumberInput && confirmedAdditionalDetailsAccountNumber) {
                const { callDetails, setCallDetails } = useCallDetailsStore.getState();
                setCallDetails({ ...callDetails, ACCOUNT_ID: confirmedAdditionalDetailsAccountNumber });
            }

            void dispatcher
                .withExtra({ partnerAgreements: customerData?.partnerAgreements })
                .dispatchBusinessEvent("CustomerPartnerAgreements");

            const hashedMdn = await getHashedValue(formattedMdn);
            const gaiaProperties = buildGaiaSessionProperties(
                {
                    mdn: formattedMdn,
                    countryCode: validatePhoneNumber(confirmedMdn).countryCode,
                    clientChannelId: customerData?.clientChannelId,
                    previousDevices: customerData?.previousDevices,
                },
                hashedMdn,
            );

            const gaiaSessionId = isVoice && taskData.agentCallId ? taskData.agentCallId : contextSessionId;

            gaiaWebsocket.sendJsonMessage({
                action: "update-session-context",
                correlationId: contextSessionId,
                sessionId: gaiaSessionId,
                sessionGroupId: contextSessionId,
                partner,
                context: {
                    properties: sanitizeGaiaSessionContextProperties(gaiaProperties),
                },
            });

            const callDetails = useCallDetailsStore.getState().callDetails;
            const profilePayload = buildSupportedProfileAnalyticsPayload({
                customerData,
                callDetails,
                currentSubscriptions,
            });

            void dispatcher
                .withMeta({ Client: partner, SkipMixpanel: true, SkipOpenSearch: true })
                .withExtra({
                    ...profilePayload,
                })
                .withIdentities({
                    MDN: formattedMdn,
                    Email: confirmedEmail,
                    AccountNumber: confirmedAccountNumber,
                    AdditionalAccountNumber: confirmedAdditionalDetailsAccountNumber,
                })
                .dispatchBusinessEvent("SupportedProfileDisplayed");

            void dispatcher
                .withMeta({ Client: partner, SkipMixpanel: true })
                .withExtra({ currentSubscriptions: currentSubscriptions ?? [] })
                .dispatchBusinessEvent("CurrentSubscriptionsCaptured");

            void dispatcher
                .withMeta({ SkipMixpanel: true, SkipEDP: true, isTest: true, SkipOpenSearch: true })
                .withExtra({
                    confirmedMdn,
                    mdnFromTask,
                })
                .dispatchBusinessEvent("MdnConfirmedByExpert");

            const authorizedUsers = Array.from(customerData?.authorizedUsers ?? []);
            if (customerData?.primaryAccountHolder) {
                authorizedUsers.push(customerData.primaryAccountHolder);
            }

            const partnerToolResult = buildPartnerToolUrl({
                partner,
                partnerToolUrl,
                isDevMode: env.isDevMode,
                customerName,
                formattedMdn,
                authorizedUsers,
                sessionId,
                determineVerificationStatus: determineCustomerVerificationStatus,
            });

            if (partnerToolResult.shouldOpen && partnerToolResult.url) {
                window.open(partnerToolResult.url, "_blank", "noopener,noreferrer");
            }

            const { pushStep } = useVerificationFlowStore.getState();
            pushStep(sessionId, "VerificationComplete");
        },
        [gaiaWebsocket, dispatcher, partner, partnerToolUrl, isVoice, taskData, contextSessionId, logger],
    );

    return {
        confirmCustomerVerification,
    };
}
